import React, { useState, useEffect, useCallback } from "react";
import {
    getDashboardMetrics,
    getPaginatedRenewals,
} from "./actions";
import { DashboardShell } from "./DashboardShell";
import { DashboardHeader } from "./DashboardHeader";
import { MetricCards } from "./MetricCards";
import { RenewalList } from "./RenewalsList";
import { DashboardMetrics, PaginatedRenewals } from "./types";
import LoadingScreen from "../../screens/LoadingScreen";
import { useIsMobile } from "../../hooks/useIsMobile";

const CampaignRenewals = () => {
    const mobile = useIsMobile(900);

    const [renewalsPage, setRenewalsPage] = useState(1);
    const [limit] = useState(10);
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [error, setError] = useState(false);
    const [renewals, setRenewals] = useState<PaginatedRenewals>();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [sortBy, setSortBy] = useState<string | undefined>();
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const fetchMetrics = async () => {
        try {
            const data = await getDashboardMetrics();
            setMetrics(data);
        } catch (error) {
            console.error("Failed to fetch dashboard metrics:", error);
            setError(true);
        }
    };

    const fetchRenewals = useCallback(
        async (p: number = renewalsPage) => {
            try {
                const data = await getPaginatedRenewals(
                    p,
                    limit,
                    searchTerm,
                    statusFilter,
                    sortBy,
                    sortDirection
                );
                setRenewals(data);
            } catch (error) {
                console.error("Failed to fetch renewals:", error);
                setError(true);
            }
        },
        [limit, renewalsPage, searchTerm, statusFilter, sortBy, sortDirection]
    );

    useEffect(() => {
        fetchMetrics();
    }, []);
    useEffect(() => {
        fetchRenewals(renewalsPage);
    }, [renewalsPage, fetchRenewals]);

    const handleRenewalSortChange = (newSortBy: string, newSortDirection: "asc" | "desc") => {
        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
    };

    if (error) return <div>Error loading metrics</div>;
    if (!metrics) return <LoadingScreen />;

    return (
        <div className={mobile ? "p-4" : "p-6"}>
            <DashboardShell>
                <DashboardHeader
                    heading="Campaign Renewal Dashboard"
                    text="Track and manage campaign renewals"
                />

                {/* Parent grid: single column on mobile, 6 columns on large */}
                <div
                    className={
                        mobile
                            ? "grid gap-6 grid-cols-1"
                            : "grid gap-6 grid-cols-1 lg:grid-cols-6"
                    }
                >
                    {/* Metric cards: full width, horizontally scrollable on mobile */}
                    <div className="col-span-full">
                        <div className={mobile ? "overflow-x-auto" : ""}>
                            <MetricCards metrics={metrics} isMobile={mobile} />
                        </div>
                    </div>

                    {/* Campaign renewals list */}
                    <RenewalList
                        renewals={renewals?.data || []}
                        fetchMetrics={fetchMetrics}
                        fetchRenewals={fetchRenewals}
                        page={renewalsPage}
                        setPage={setRenewalsPage}
                        limit={limit}
                        total={renewals?.total || 0}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        isMobile={mobile}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        onSortChange={handleRenewalSortChange}
                    />
                </div>
            </DashboardShell>
        </div>
    );
};

export default CampaignRenewals;
